import React, { useState, useMemo } from "react";
import { useParams } from "react-router-dom";
import { useWsNavigate } from "../../hooks/useWsNavigate";
import { useAuth } from "../../contexts/AuthContext";
import { stressTestsAPI } from "../../services/api/stressTests";
import useSetPageTitle from "../../hooks/useSetPageTitle";
import PageHeader from "../../components/layout/PageHeader";

// Client-side floor, mirroring AiConfigService.min_stress_test_calls. There
// is no live source for this on the frontend: the quote endpoint
// (StressTests::CostEstimator) returns only credits/breakdown, never
// min_stress_test_calls, so this is a static guess, not a fetched value.
// The backend's own inline validation on create (422) is the real
// enforcement — this only pre-empts the obviously-invalid case client-side.
const DEFAULT_MIN_CALLS = 10;

// UI-side cap so nobody accidentally fat-fingers a huge run. Backend-enforced
// limits (fleet sizing, admission config) are separate and still apply.
const MAX_CALLS = 100;

const LOAD_MODE_OPTIONS = [
  {
    value: "wave",
    label: "Wave",
    desc: "Launch all calls at once, then let them finish — a spike test.",
  },
  {
    value: "sustained",
    label: "Sustained",
    desc: "Hold N calls live for a set duration; as each ends, another starts — a soak test.",
  },
];

const TRANSPORT_OPTIONS = [
  {
    value: "websocket",
    label: "Web Call",
    desc: "Callers connect over the web (WebSocket / WebRTC).",
  },
  {
    value: "pstn",
    label: "Phone Call",
    desc: "Callers dial in over the phone network (Twilio).",
  },
];

// A selectable radio-button card — same pattern as the agent Channels → Web
// Channel provider picker (CreateAgent.js): radio + in-card title + one-line
// description, so the left column can stay a short label instead of carrying
// the whole explanation.
function RadioCard({ name, value, checked, onChange, label, desc, badge }) {
  return (
    <label
      className={`flex items-start gap-3 flex-1 border rounded-lg px-4 py-3 cursor-pointer transition-colors ${
        checked ? "border-primary bg-primary/5" : "border-border hover:border-input"
      }`}
    >
      <input
        type="radio"
        name={name}
        value={value}
        checked={checked}
        onChange={onChange}
        className="mt-0.5 h-4 w-4 shrink-0"
      />
      <span className="min-w-0">
        <span className="flex items-center gap-2">
          <span className="text-sm font-medium text-foreground">{label}</span>
          {badge && (
            <span className="text-[10px] font-semibold uppercase tracking-wide text-primary bg-primary/10 px-1.5 py-0.5 rounded-full">
              {badge}
            </span>
          )}
        </span>
        <span className="block text-xs text-muted-foreground mt-0.5">{desc}</span>
      </span>
    </label>
  );
}

export function StressTestSetup() {
  useSetPageTitle("Run Stress Test");
  const { scenarioId } = useParams();
  const navigate = useWsNavigate();
  const { currentWorkspace } = useAuth();
  const clientId = currentWorkspace?.id;

  const [count, setCount] = useState("");
  const [loadMode, setLoadMode] = useState("wave");
  const [holdDurationS, setHoldDurationS] = useState("60");
  const [transport, setTransport] = useState("websocket");

  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  const countNum = parseInt(count, 10);
  const hasValidCount = count !== "" && !Number.isNaN(countNum) && countNum > 0;
  const minCalls = DEFAULT_MIN_CALLS;
  const belowMin = !hasValidCount || countNum < minCalls;
  const aboveMax = hasValidCount && countNum > MAX_CALLS;

  // The submission payload.
  const basePayload = useMemo(
    () => ({
      scenario_id: scenarioId,
      target_concurrency: hasValidCount ? countNum : undefined,
      load_mode: loadMode,
      transport,
      ...(loadMode === "sustained"
        ? { hold_duration_s: parseInt(holdDurationS, 10) || undefined }
        : {}),
    }),
    [scenarioId, hasValidCount, countNum, loadMode, transport, holdDurationS]
  );

  const canProceed = hasValidCount && !belowMin && !aboveMax && !submitting;

  const handleCancel = () => {
    navigate("/live-simulations/scenario");
  };

  const handleProceed = async () => {
    if (!canProceed) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      const res = await stressTestsAPI.createStressTest(basePayload, clientId);
      const id = res?.data?.stress_test?.id;
      navigate(`/stress-test/${id}`);
    } catch (err) {
      const status = err?.response?.status;
      const message = err?.response?.data?.error;
      if (status === 422) {
        setSubmitError(message || `Minimum is ${minCalls} concurrent calls.`);
      } else if (status === 402) {
        setSubmitError(message || "Insufficient credits to run this stress test.");
      } else {
        setSubmitError(message || "Failed to create stress test. Please try again.");
      }
      setSubmitting(false);
    }
  };

  return (
    <div className="px-4">
      <PageHeader description="Configure concurrency, load mode, and transport for this scenario's stress test." />

      <div className="max-w-8xl">
        <div className="bg-card rounded-lg border border-border">
          <div className="p-4">
            <div className="space-y-8">
              {/* Concurrent calls */}
              <div className="grid grid-cols-3 gap-8">
                <div className="col-span-1">
                  <h3 className="text-sm font-medium text-foreground">Concurrent Calls</h3>
                  <p className="mt-1 text-sm text-muted-foreground">
                    Number of simultaneous calls to simulate against this scenario.
                  </p>
                </div>
                <div className="col-span-2">
                  <label htmlFor="stress-test-count" className="sr-only">
                    Concurrent Calls
                  </label>
                  <input
                    id="stress-test-count"
                    type="number"
                    min="1"
                    max={MAX_CALLS}
                    value={count}
                    onChange={(e) => setCount(e.target.value)}
                    placeholder="e.g. 50"
                    className="block w-full max-w-xs rounded-md border-input shadow-sm focus:border-primary focus:ring-primary text-sm bg-background text-foreground"
                  />
                  {count !== "" && belowMin && (
                    <p className="mt-1 text-xs text-destructive">
                      Minimum is {minCalls} concurrent calls.
                    </p>
                  )}
                  {count !== "" && aboveMax && (
                    <p className="mt-1 text-xs text-destructive">
                      Maximum is {MAX_CALLS} concurrent calls.
                    </p>
                  )}
                </div>
              </div>

              {/* Load mode */}
              <div className="grid grid-cols-3 gap-8">
                <div className="col-span-1">
                  <h3 className="text-sm font-medium text-foreground">Load Mode</h3>
                  <p className="mt-1 text-sm text-muted-foreground">
                    How calls are scheduled over the run.
                  </p>
                </div>
                <div className="col-span-2">
                  <div className="flex flex-col sm:flex-row gap-3">
                    {LOAD_MODE_OPTIONS.map((opt) => (
                      <RadioCard
                        key={opt.value}
                        name="loadMode"
                        value={opt.value}
                        checked={loadMode === opt.value}
                        onChange={() => setLoadMode(opt.value)}
                        label={opt.label}
                        desc={opt.desc}
                      />
                    ))}
                  </div>
                  {loadMode === "sustained" && (
                    <div className="mt-3 max-w-xs">
                      <label
                        htmlFor="stress-test-hold-duration"
                        className="block text-xs font-medium text-muted-foreground mb-1.5"
                      >
                        Hold Duration (seconds)
                      </label>
                      <input
                        id="stress-test-hold-duration"
                        type="number"
                        min="1"
                        value={holdDurationS}
                        onChange={(e) => setHoldDurationS(e.target.value)}
                        className="block w-full rounded-md border-input shadow-sm focus:border-primary focus:ring-primary text-sm bg-background text-foreground"
                      />
                    </div>
                  )}
                </div>
              </div>

              {/* Transport */}
              <div className="grid grid-cols-3 gap-8">
                <div className="col-span-1">
                  <h3 className="text-sm font-medium text-foreground">Transport</h3>
                  <p className="mt-1 text-sm text-muted-foreground">
                    How simulated calls reach the agent.
                  </p>
                </div>
                <div className="col-span-2">
                  <div className="flex flex-col sm:flex-row gap-3">
                    {TRANSPORT_OPTIONS.map((opt) => (
                      <RadioCard
                        key={opt.value}
                        name="transport"
                        value={opt.value}
                        checked={transport === opt.value}
                        onChange={() => setTransport(opt.value)}
                        label={opt.label}
                        desc={opt.desc}
                      />
                    ))}
                  </div>
                </div>
              </div>

              <p className="text-xs text-muted-foreground">
                Use this only to stress-test your bot's capacity under load. For normal
                evaluation, just run the scenario as usual — don't use a stress test.
              </p>

              {submitError && (
                <div className="rounded-lg border border-destructive/30 bg-destructive/10 px-3 py-2.5 text-sm text-destructive">
                  {submitError}
                </div>
              )}
            </div>
          </div>

          <div className="flex justify-end gap-2 px-4 py-3 border-t border-border">
            <button
              type="button"
              onClick={handleCancel}
              className="px-4 py-2.5 text-sm font-medium text-muted-foreground border border-border rounded-lg hover:bg-muted transition-colors"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleProceed}
              disabled={!canProceed}
              className="px-5 py-2.5 text-sm font-medium rounded-lg text-primary-foreground bg-primary hover:bg-primary/90 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {submitting ? "Creating..." : "Proceed"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default StressTestSetup;
